import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RfqService from '../../Services/rfq_service';
import ProductService from '../../Services/products_service';

function HomeContent() {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [productCount, setProductCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAcceptedRfqs, setShowAcceptedRfqs] = useState(false);
  const [showQuotations, setShowQuotations] = useState(true); // Default to true for initial view
  const [showRejectedQuotations, setShowRejectedQuotations] = useState(false);
  const [showActiveOrders, setShowActiveOrders] = useState(false); // New state for active orders table

  const fetchData = async () => {
    try {
      setLoading(true);
      const rfqResponse = await RfqService.getAllRfqs();
      if (rfqResponse.success) setRfqs(rfqResponse.data || []);
      const salesOrderResponse = await RfqService.getAllSalesOrders();
      if (salesOrderResponse.success) setSalesOrders(salesOrderResponse.data || []);
      const quotationResponse = await RfqService.getAllQuotations();
      if (quotationResponse.success) setQuotations(quotationResponse.data || []);
      const countResponse = await ProductService.getProductCount();
      if (countResponse.success) setProductCount(countResponse.count || null);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(async () => {
      try {
        const [rfqResponse, salesOrderResponse, quotationResponse, countResponse] = await Promise.all([
          RfqService.getAllRfqs(),
          RfqService.getAllSalesOrders(),
          RfqService.getAllQuotations(),
          ProductService.getProductCount(),
        ]);
        if (rfqResponse.success) setRfqs(rfqResponse.data || []);
        if (salesOrderResponse.success) setSalesOrders(salesOrderResponse.data || []);
        if (quotationResponse.success) setQuotations(quotationResponse.data || []);
        if (countResponse.success) setProductCount(countResponse.count || null);
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 30000);
    return () => clearInterval(intervalId);
  }, []);

  const acceptedRfqs = rfqs.filter(rfq => rfq.status === 'Accepted').length;
  const rejectedQuotations = quotations.filter(q => q.quatation?.status === 'Rejected').length;
  const pendingQuotations = quotations.filter(q => q.quatation?.status === 'Pending').length;
  const activeOrders = salesOrders.filter(order =>
    ["In Synthesis", "Ready To Pack", "Packing"].includes(order.salesOrderStatus)
  ).length;

  const handleAcceptedRfqsClick = () => {
    setShowAcceptedRfqs(!showAcceptedRfqs);
    if (!showAcceptedRfqs) {
      setShowQuotations(false);
      setShowRejectedQuotations(false);
      setShowActiveOrders(false);
    }
  };

  const handleQuotationsClick = () => {
    setShowQuotations(!showQuotations);
    if (!showQuotations) {
      setShowAcceptedRfqs(false);
      setShowRejectedQuotations(false);
      setShowActiveOrders(false);
    }
  };

  const handleRejectedQuotationsClick = () => {
    setShowRejectedQuotations(!showRejectedQuotations);
    if (!showRejectedQuotations) {
      setShowAcceptedRfqs(false);
      setShowQuotations(false);
      setShowActiveOrders(false);
    }
  };

  const handleActiveOrdersClick = () => {
    setShowActiveOrders(!showActiveOrders);
    if (!showActiveOrders) {
      setShowAcceptedRfqs(false);
      setShowQuotations(false);
      setShowRejectedQuotations(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#2b6e73]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 py-10 bg-gray-50">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center mb-6"></div>

      <h2 className="text-2xl font-bold text-[#2b6e73] mb-4">
        Welcome to Varanous Lab Customer Dashboard
      </h2>
      <p className="text-gray-600 mb-8">
        Here’s the summary of recent activity
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <NewDataCard
          title="Accepted RFQs"
          value={acceptedRfqs}
          subtitle="Updated recently"
          isLoading={loading}
          isActive={showAcceptedRfqs}
          onClick={handleAcceptedRfqsClick}
        />
        <NewDataCard
          title="Rejected Quotations"
          value={rejectedQuotations}
          subtitle={`${rejectedQuotations} rejected`}
          isLoading={loading}
          isActive={showRejectedQuotations}
          onClick={handleRejectedQuotationsClick}
        />
        <NewDataCard
          title="Quotations"
          value={quotations.length}
          subtitle={`${pendingQuotations} require payment`}
          isLoading={loading}
          isActive={showQuotations}
          onClick={handleQuotationsClick}
        />
        <NewDataCard
          title="Active Orders"
          value={activeOrders}
          subtitle={`${activeOrders} active order${activeOrders !== 1 ? 's' : ''}`}
          isLoading={loading}
          isActive={showActiveOrders}
          onClick={handleActiveOrdersClick}
        />
      </div>
      {showAcceptedRfqs && (
        <AcceptedRfqsTable rfqs={rfqs.filter(rfq => rfq.status === 'Accepted')} />
      )}
      {showQuotations && (
        <QuotationsTable quotations={quotations} rfqs={rfqs} />
      )}
      {showRejectedQuotations && (
        <RejectedQuotationsTable quotations={quotations.filter(q => q.quatation?.status === 'Rejected')} rfqs={rfqs} />
      )}
      {showActiveOrders && (
        <ActiveOrdersTable orders={salesOrders.filter(order =>
          ["In Synthesis", "Ready To Pack", "Packing"].includes(order.salesOrderStatus)
        )} />
      )}
    </div>
  );
}

const NewDataCard = ({ title, value, subtitle, isLoading, isActive, onClick }) => {
  return (
    <div
      className={`bg-white border-2 ${isActive ? 'border-[#2b6e73] bg-[#e6f3f4]' : 'border-gray-300'} p-4 rounded-lg shadow-lg w-70 transform hover:scale-105 transition relative cursor-${onClick ? 'pointer' : 'default'}`}
      onClick={onClick}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 bg-opacity-50 flex items-center justify-center rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-[#2b6e73]"></div>
        </div>
      )}
      <p
        className="text-1xl font-bold text-[#2b6e73] relative z-10Corollary" style={{ maxWidth: '100%' }}>
        {isLoading ? '...' : `${isActive ? '< ' : ''}${value} ${title}${isActive ? ' >' : ''}`}
      </p>
      <p className="text-sm text-gray-500 mt-1 relative z-10">{subtitle}</p>
    </div>
  );
};

const QuotationsTable = ({ quotations, rfqs }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const quotationsPerPage = 10;
  const currentDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, ' ');

  const formatDate = (dateString) => {
    return dateString
      ? new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, ' ')
      : currentDate;
  };

  const sortedQuotations = [...quotations].sort((a, b) => {
    const orderNoA = a.quatation?.orderNo ? parseInt(a.quatation.orderNo.replace(/[^0-9]/g, '')) : 0;
    const orderNoB = b.quatation?.orderNo ? parseInt(b.quatation.orderNo.replace(/[^0-9]/g, '')) : 0;
    return orderNoB - orderNoA;
  }).map(quote => ({
    ...quote,
    displayDate: formatDate(quote.quatation?.quatationDate || rfqs.find(r => r.rfqNo === quote.quatation?.rfqNo)?.createdAt)
  }));

  const indexOfLastQuotation = currentPage * quotationsPerPage;
  const indexOfFirstQuotation = indexOfLastQuotation - quotationsPerPage;
  const currentQuotations = sortedQuotations.slice(indexOfFirstQuotation, indexOfLastQuotation);
  const totalPages = Math.ceil(sortedQuotations.length / quotationsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const maxPageButtons = 5;
  const halfMax = Math.floor(maxPageButtons / 2);
  let startPage = Math.max(1, currentPage - halfMax);
  let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
  if (endPage - startPage + 1 < maxPageButtons) {
    startPage = Math.max(1, endPage - maxPageButtons + 1);
  }
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-xl font-semibold text-[#2b6e73] mb-4">Recent Quotations</h2>
      {sortedQuotations.length > 0 ? (
        <>
          <table className="w-full text-left table-auto">
            <thead>
              <tr className="bg-[#2b6e73] text-white">
                <th className="p-3 font-semibold">Quote No</th>
                <th className="p-3 font-semibold">Enquiry ID</th>
                <th className="p-3 font-semibold">Products</th>
                <th className="p-3 font-semibold" style={{ width: '120px' }}>Order Date</th>
                <th className="p-3 font-semibold">Amount</th>
                <th className="p-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {currentQuotations.map((quote, index) => {
                const quotation = quote.quatation || {};
                const products = quotation.quotationProducts || [];
                const productNames = products.map(product => product.productName).join(', ');
                const status = quotation.status || 'N/A';
                let statusClass = 'bg-gray-100 text-gray-700';
                if (status === 'Completed') statusClass = 'bg-green-100 text-green-700';
                else if (status === 'Rejected') statusClass = 'bg-red-100 text-red-700';
                else if (status === 'Accepted') statusClass = 'bg-blue-100 text-blue-700';
                else if (status === 'Pending') statusClass = 'bg-yellow-100 text-yellow-700';

                const matchingRfq = rfqs.find(r => r.rfqNo === quotation.rfqNo);
                const enquiryId = matchingRfq ? matchingRfq.rfqNo : 'N/A';
                const orderDate = quote.displayDate;

                return (
                  <tr key={index} className="border-b hover:bg-gray-100 transition">
                    <td className="p-3">{quotation.orderNo || 'N/A'}</td>
                    <td className="p-3">{enquiryId}</td>
                    <td className="p-3">{productNames || 'N/A'}</td>
                    <td className="p-3" style={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orderDate}</td>
                    <td className="p-3">{quotation.finalAmount ? `₹${quotation.finalAmount}` : 'N/A'}</td>
                    <td className="p-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusClass}`}>
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 && (
  <div className="flex justify-center mt-4 items-center gap-2">
    <button
      onClick={() => paginate(1)}
      disabled={currentPage === 1}
      className={`w-16 h-8 flex items-center justify-center rounded-full ${
        currentPage === 1
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110'
      }`}
    >
      First
    </button>
    <button
      onClick={prevPage}
      disabled={currentPage === 1}
      className={`w-8 h-8 flex items-center justify-center rounded-full ${
        currentPage === 1
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110'
      }`}
    >
      ←
    </button>
    {startPage > 1 && (
      <button
        onClick={() => paginate(1)}
        className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
      >
        1
      </button>
    )}
    {startPage > 2 && <span className="text-gray-500">...</span>}
    {pageNumbers.map((page) => (
      <button
        key={page}
        onClick={() => paginate(page)}
        className={`w-8 h-8 flex items-center justify-center rounded-full ${
          currentPage === page
            ? 'bg-[#2b6e73] text-white'
            : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white'
        } transition shadow-sm hover:scale-110`}
      >
        {page}
      </button>
    ))}
    {endPage < totalPages - 1 && <span className="text-gray-500">...</span>}
    {endPage < totalPages && (
      <button
        onClick={() => paginate(totalPages)}
        className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
      >
        {totalPages}
      </button>
    )}
    <button
      onClick={nextPage}
      disabled={currentPage === totalPages}
      className={`w-8 h-8 flex items-center justify-center rounded-full ${
        currentPage === totalPages
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110'
      }`}
    >
      →
    </button>
    <button
      onClick={() => paginate(totalPages)}
      disabled={currentPage === totalPages}
      className={`w-16 h-8 flex items-center justify-center rounded-full ${
        currentPage === totalPages
          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
          : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110'
      }`}
    >
      Last
    </button>
  </div>
)}
        </>
      ) : (
        <p className="text-gray-500 text-center py-4">No quotations available</p>
      )}
    </div>
  );
};

const AcceptedRfqsTable = ({ rfqs }) => {
  const formatDate = (dateString) => {
    return new Date(dateString)
      .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      .replace(/ /g, ' ');
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-xl font-semibold text-[#2b6e73] mb-4">Accepted RFQs</h2>
      {rfqs.length > 0 ? (
        <table className="w-full text-left table-auto">
          <thead>
            <tr className="bg-[#2b6e73] text-white">
              <th className="p-3 font-semibold">Enquiry ID</th>
              <th className="p-3 font-semibold">Customer Name</th>
              <th className="p-3 font-semibold" style={{ width: '120px' }}>Order Date</th>
              <th className="p-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {rfqs.map((rfq, index) => (
              <tr key={index} className="border-b hover:bg-gray-100 transition">
                <td className="p-3">{rfq.rfqNo || 'N/A'}</td>
                <td className="p-3">{rfq.customerName || 'N/A'}</td>
                <td className="p-3" style={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rfq.createdAt ? formatDate(rfq.createdAt) : 'N/A'}</td>
                <td className="p-3">
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                    Accepted
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-gray-500 text-center py-4">No accepted RFQs available</p>
      )}
    </div>
  );
};

const RejectedQuotationsTable = ({ quotations, rfqs }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const quotationsPerPage = 10;

  const formatDate = (dateString) => {
    return new Date(dateString)
      .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      .replace(/ /g, ' ');
  };

  const sortedQuotations = [...quotations].sort((a, b) => {
    const dateA = a.quatation?.quatationDate ? new Date(a.quatation.quatationDate) : 
                  rfqs.find(r => r.rfqNo === a.quatation?.rfqNo)?.createdAt ? new Date(rfqs.find(r => r.rfqNo === a.quatation?.rfqNo).createdAt) : new Date(0);
    const dateB = b.quatation?.quatationDate ? new Date(b.quatation.quatationDate) : 
                  rfqs.find(r => r.rfqNo === b.quatation?.rfqNo)?.createdAt ? new Date(rfqs.find(r => r.rfqNo === b.quatation?.rfqNo).createdAt) : new Date(0);
    return dateB - dateA;
  });

  const indexOfLastQuotation = currentPage * quotationsPerPage;
  const indexOfFirstQuotation = indexOfLastQuotation - quotationsPerPage;
  const currentQuotations = sortedQuotations.slice(indexOfFirstQuotation, indexOfLastQuotation);
  const totalPages = Math.ceil(sortedQuotations.length / quotationsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const maxPageButtons = 5;
  const halfMax = Math.floor(maxPageButtons / 2);
  let startPage = Math.max(1, currentPage - halfMax);
  let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
  if (endPage - startPage + 1 < maxPageButtons) {
    startPage = Math.max(1, endPage - maxPageButtons + 1);
  }
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-xl font-semibold text-[#2b6e73] mb-4">Rejected Quotations</h2>
      {sortedQuotations.length > 0 ? (
        <>
          <table className="w-full text-left table-auto">
            <thead>
              <tr className="bg-[#2b6e73] text-white">
                <th className="p-3 font-semibold">Quote No</th>
                <th className="p-3 font-semibold">Products</th>
                <th className="p-3 font-semibold" style={{ width: '120px' }}>Order Date</th>
                <th className="p-3 font-semibold">Amount</th>
                <th className="p-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {currentQuotations.map((quote, index) => {
                const quotation = quote.quatation || {};
                const products = quotation.quotationProducts || [];
                const productNames = products.map(product => product.productName).join(', ');
                const status = quotation.status || 'N/A';
                let statusClass = 'bg-red-100 text-red-700';

                const matchingRfq = rfqs.find(r => r.rfqNo === quotation.rfqNo);
                const enquiryId = matchingRfq ? matchingRfq.rfqNo : 'N/A';
                const orderDate = quotation.quatationDate || (matchingRfq ? formatDate(matchingRfq.createdAt) : 'N/A');

                return (
                  <tr key={index} className="border-b hover:bg-gray-100 transition">
                    <td className="p-3">{quotation.orderNo || 'N/A'}</td>
                    <td className="p-3">{productNames || 'N/A'}</td>
                    <td className="p-3" style={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{orderDate}</td>
                    <td className="p-3">{quotation.finalAmount ? `₹${quotation.finalAmount}` : 'N/A'}</td>
                    <td className="p-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusClass}`}>
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex justify-center mt-4 items-center gap-2">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  currentPage === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110'
                }`}
              >
                ←
              </button>
              {startPage > 1 && (
                <button
                  onClick={() => paginate(1)}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
                >
                  1
                </button>
              )}
              {startPage > 2 && <span className="text-gray-500">...</span>}
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => paginate(page)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full ${
                    currentPage === page
                      ? 'bg-[#2b6e73] text-white'
                      : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white'
                  } transition shadow-sm hover:scale-110`}
                >
                  {page}
                </button>
              ))}
              {endPage < totalPages - 1 && <span className="text-gray-500">...</span>}
              {endPage < totalPages && (
                <button
                  onClick={() => paginate(totalPages)}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
                >
                  {totalPages}
                </button>
              )}
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  currentPage === totalPages
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110'
                }`}
              >
                →
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-gray-500 text-center py-4">No rejected quotations available</p>
      )}
    </div>
  );
};

const ActiveOrdersTable = ({ orders }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  const formatDate = (dateString) => {
    if (!dateString || isNaN(new Date(dateString).getTime())) {
      return 'N/A';
    }
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).replace(/ /g, ' ');
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const dateA = a.orderDate ? new Date(a.orderDate) : new Date(0);
    const dateB = b.orderDate ? new Date(b.orderDate) : new Date(0);
    return dateB - dateA;
  });

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(sortedOrders.length / ordersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const maxPageButtons = 5;
  const halfMax = Math.floor(maxPageButtons / 2);
  let startPage = Math.max(1, currentPage - halfMax);
  let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
  if (endPage - startPage + 1 < maxPageButtons) {
    startPage = Math.max(1, endPage - maxPageButtons + 1);
  }
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-xl font-semibold text-[#2b6e73] mb-4">Active Orders</h2>
      {currentOrders.length > 0 ? (
        <>
          <table className="w-full text-left table-auto">
            <thead>
              <tr className="bg-[#2b6e73] text-white">
                <th className="p-3 font-semibold text-center">Order No</th>
                <th className="p-3 font-semibold text-center">RFQ No</th>
                <th className="p-3 font-semibold text-center">Customer Name</th>
                <th className="p-3 font-semibold text-center">Assignee Name</th>
                <th className="p-3 font-semibold text-center">Order Date</th>
                <th className="p-3 font-semibold text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order, index) => (
                <tr key={index} className="border-b hover:bg-gray-100 transition">
                  <td className="p-3 text-center">{order.salesOrderNo || 'N/A'}</td>
                  <td className="p-3 text-center">{order.rfqNo || 'N/A'}</td>
                  <td className="p-3 text-center">{order.customerName || 'N/A'}</td>
                  <td className="p-3 text-center">{order.assigneeName || 'N/A'}</td>
                  <td className="p-3 text-center" style={{ width: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatDate(order.orderDate)}
                  </td>
                  <td className="p-3 text-center">
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        order.salesOrderStatus === "In Synthesis"
                          ? "bg-yellow-100 text-yellow-700"
                          : order.salesOrderStatus === "Ready To Pack"
                          ? "bg-orange-100 text-orange-700"
                          : order.salesOrderStatus === "Packing"
                          ? "bg-red-100 text-red-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {order.salesOrderStatus || 'N/A'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex justify-center mt-4 items-center gap-2">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  currentPage === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110'
                }`}
              >
                ←
              </button>
              {startPage > 1 && (
                <button
                  onClick={() => paginate(1)}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
                >
                  1
                </button>
              )}
              {startPage > 2 && <span className="text-gray-500">...</span>}
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => paginate(page)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full ${
                    currentPage === page
                      ? 'bg-[#2b6e73] text-white'
                      : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white'
                  } transition shadow-sm hover:scale-110`}
                >
                  {page}
                </button>
              ))}
              {endPage < totalPages - 1 && <span className="text-gray-500">...</span>}
              {endPage < totalPages && (
                <button
                  onClick={() => paginate(totalPages)}
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110"
                >
                  {totalPages}
                </button>
              )}
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`w-8 h-8 flex items-center justify-center rounded-full ${
                  currentPage === totalPages
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'border border-[#2b6e73] text-[#2b6e73] hover:bg-[#2b6e73]/80 hover:text-white transition shadow-sm hover:scale-110'
                }`}
              >
                →
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-gray-500 text-center py-4">No active orders available</p>
      )}
    </div>
  );
};

export default HomeContent;